import AiContextAction from "@/containers/facility/AiAssistant/AiContextAction";
import globalMessage from "@/utils/globalMessage";
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Form, Input, Button, Spin, Modal, Divider, Row, Col, Badge, Checkbox, Typography, Table, InputNumber } from "antd";
import DetailTabs from "@components/DetailTabs";
import { api } from "@redux/api";
import { WorkorderComment } from "@containers/facility/Workorder/WorkorderComment";
import ResponsiveFileManagement from "@components/FileManagement/ResponsiveFileManagement";
import RecordAuditTimeline from "@components/Table/RecordAuditLog";
import { useSelector } from "react-redux";
import moment from "moment";

import { useKeepAliveContext } from "@/hooks/use-keep-alive-context";
import { useRole } from "@/hooks/use-role";
import { EventName, useEventBus, emitEvent } from "@/hooks/useEventBus";
import _ from "lodash";
import "./index.css";
import { useAiAssistantAccess, useAuthEditable, useAuthFullAccess } from "@/hooks/use-auth";
import ContractCompanyDetailModal from "@/containers/contract/ContractCompanyDetailModal";
import WorkorderDraftPanel from "../WorkorderDraftPanel";
import WorkorderRelatedTab from "./WorkorderRelatedTab";
import { sameDraftScope } from "../draftValues.mjs";
import { setRecordEditing, RECORD_SAVED_EVENT } from "@/containers/facility/AiAssistant/recordEditState.mjs";
import { buildFacilityPath, getFacilityContext } from "@/utils/route-context";
import {
  formatContractRef,
  formatServiceRequestRef,
  formatWorkorderRef,
} from "@/utils/entity-ref";
import Select from "@components/ResponsiveSelect";
import DatePicker from "@components/ResponsiveDatePicker";
import {
  formatWorkOrderDomainValue,
  formatWorkOrderStatus,
  getSemanticCapabilities,
  getWorkOrderDomainValues,
  getWorkOrderInitialStatus,
  getWorkOrderStatusValues,
  getWorkOrderTerminalStatuses,
  loadSemanticCapabilities,
} from "@/services/semanticCapabilities";
import {
  WORKORDER_CREATE_PREFILL_KEY,
  CONTRACT_CREATE_PREFILL_KEY,
  clearFlowPrefill,
  readAndClearFlowPrefill,
  writeFlowPrefill,
} from "@/utils/flow-prefill";

const { TabPane } = DetailTabs;
const { TextArea } = Input;
const { Option } = Select;
const { Text: AntText } = Typography;

/** PM 检查项 options：模板可能存数组或逗号分隔字符串 */
function normalizePmChecklistOptions(raw) {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw.map((x) => String(x));
  if (typeof raw === "string") {
    return raw
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
  }
  return [];
}

function formatPmChecklistUnit(unit) {
  if (unit == null) return null;
  const s = String(unit).trim();
  return s || null;
}

/**
 * Template row / column keys（与 Functional Testing 一致：行用 label，列用 id）
 */
function templateRowKey(f) {
  if (f == null) return "";
  const k = f.name != null ? String(f.name) : f.label != null ? String(f.label) : "";
  return k.trim();
}

/**
 * 将工单已保存的 checklist items 与模板行合并（仅无 columns 的旧版纵向卡片 UI）。
 */
function mergePmChecklistDisplayRows(result, templateFields) {
  if (!result || !Array.isArray(result)) return [];
  const tpl = Array.isArray(templateFields)
    ? templateFields
    : templateFields && typeof templateFields === "object" && Array.isArray(templateFields.rows)
      ? templateFields.rows
      : [];
  const byKey = {};
  tpl.forEach((f) => {
    const k = templateRowKey(f);
    if (k) byKey[k] = f;
  });
  return result.map((row, i) => {
    const rowNameKey =
      row != null
        ? String(row.name ?? row.label ?? "").trim()
        : "";
    const t = rowNameKey && byKey[rowNameKey] ? byKey[rowNameKey] : tpl[i] || {};
    const mergedUnit =
      row?.unit != null && String(row.unit).trim() !== ""
        ? String(row.unit).trim()
        : t?.unit != null && String(t.unit).trim() !== ""
          ? String(t.unit).trim()
          : null;
    return {
      ...t,
      ...row,
      name: row?.name != null && row.name !== "" ? row.name : (row?.label != null ? row.label : t.name ?? t.label),
      type: t?.type ?? row?.type,
      unit: mergedUnit,
      options: row?.options != null ? row.options : t.options,
      required: row?.required != null ? row.required : t.required,
    };
  });
}

/** 与模板行、列对齐的填写行（每行含各 column.id 的值） */
function initPmChecklistItems(fieldRows, fieldColumns, raw) {
  const saved = Array.isArray(raw?.items) ? raw.items : [];
  return fieldRows.map((row, idx) => {
    const label =
      row.label != null ? String(row.label) : row.name != null ? String(row.name) : "";
    const rid = row.id != null ? String(row.id) : `row_${idx}`;
    const preset = saved[idx] && typeof saved[idx] === "object" ? { ...saved[idx] } : {};
    const item = {
      ...row,
      ...preset,
      id: preset.id != null ? preset.id : rid,
      label,
      name: preset.name != null ? preset.name : label,
    };
    (fieldColumns || []).forEach((col) => {
      const cid = col.id;
      const ct = String(col.type ?? "text").toLowerCase().trim();
      if (cid != null && item[cid] === undefined) {
        if (ct === "label" || ct === "na") return;
        item[cid] = ct === "boolean" ? false : "";
      }
    });
    return item;
  });
}

/**
 * 将接口返回的 pm_checklist_result 规范为可编辑结构，包含 fields.rows/columns 与 items。
 */
function ensurePmChecklistShape(raw) {
  if (raw == null) return null;
  if (Array.isArray(raw)) {
    return {
      checklist_name: "",
      checklist_description: "",
      fields: { rows: [], columns: [], cells: {} },
      items: JSON.parse(JSON.stringify(raw)),
    };
  }
  const base = JSON.parse(JSON.stringify(raw));
  const fields = base.fields;
  if (fields && typeof fields === "object" && !Array.isArray(fields)) {
    const rows = Array.isArray(fields.rows) ? fields.rows : [];
    const cols = Array.isArray(fields.columns) ? fields.columns : [];
    const tmplCells =
      fields.cells != null && typeof fields.cells === "object" && !Array.isArray(fields.cells)
        ? fields.cells
        : {};
    const prevItems = Array.isArray(base.items) ? base.items : null;
    const { rows: _fr, columns: _fc, cells: _fcells, ...fieldsMeta } = fields;
    base.fields = { ...fieldsMeta, rows, columns: cols, cells: tmplCells };
    base.items = initPmChecklistItems(rows, cols, { items: prevItems || undefined });
    return base;
  }
  if (Array.isArray(fields)) {
    return {
      checklist_name: base.checklist_name ?? "",
      checklist_description: base.checklist_description ?? "",
      fields: { rows: [], columns: [], cells: {} },
      items: JSON.parse(JSON.stringify(fields)),
    };
  }
  if (!Array.isArray(base.items)) base.items = [];
  return base;
}

function safePmCellDisplay(val) {
  if (val === null || val === undefined) return "";
  if (typeof val === "boolean") return val ? "Yes" : "No";
  if (typeof val === "object") return JSON.stringify(val);
  return String(val);
}

/**
 * PM Checklist 展示模式：有 columns 时用表格（rows × columns）；否则用旧版卡片列表。
 */
function getPmChecklistViewModel(raw) {
  const shaped = ensurePmChecklistShape(raw);
  if (!shaped || !Array.isArray(shaped.items)) return { mode: "empty", tableColumns: [], items: [], cards: [] };
  const cols = shaped.fields?.columns || [];
  if (cols.length > 0) {
    return { mode: "table", shaped, columns: cols, items: shaped.items };
  }
  const merged = mergePmChecklistDisplayRows(shaped.items, shaped.fields);
  return { mode: "cards", shaped, cards: merged };
}

const PM_CHECKLIST_FIELD_CARD_STYLE = {
  border: "1px solid #e8e8e8",
  borderRadius: 8,
  padding: "14px 16px",
  background: "#fafafa",
  height: "100%",
};

const toPositiveId = (value) => {
  const id = Number(value);
  return Number.isInteger(id) && id > 0 ? id : null;
};

const WorkorderDetail = ({ isAdd = false }) => {
  const { id, companyId: routeCompanyId, facilityId: routeFacilityId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const [semanticCapabilities, setSemanticCapabilities] = useState(
    getSemanticCapabilities()
  );
  const WORK_ORDER_STATUS_VALUES = getWorkOrderStatusValues(semanticCapabilities);
  const WORK_ORDER_INITIAL_STATUS = getWorkOrderInitialStatus(semanticCapabilities);
  const WORK_ORDER_TERMINAL_STATUSES = getWorkOrderTerminalStatuses(
    semanticCapabilities
  );
  const workOrderTypeValues = getWorkOrderDomainValues(
    "workorder_type",
    semanticCapabilities
  );
  const workOrderPriorityValues = getWorkOrderDomainValues(
    "priority",
    semanticCapabilities
  );
  const workOrderEnergyImpactValues = getWorkOrderDomainValues(
    "energy_impact",
    semanticCapabilities
  );
  useEffect(() => {
    let mounted = true;
    loadSemanticCapabilities().then((capabilities) => {
      if (mounted) setSemanticCapabilities(capabilities);
    });
    return () => {
      mounted = false;
    };
  }, []);
  /** 必须订阅表单字段；若仅用 shouldUpdate(workorder_type)，工单切换时 workorderData 已变但子树不重绘，PM Checklist 会空白或陈旧 */
  const watchedWorkorderType = Form.useWatch("workorder_type", form);
  const editable = useAuthEditable('workorder');
  const canUseAi = useAiAssistantAccess();
  const contractEditable = useAuthEditable('contract');
  const fullAccess = useAuthFullAccess('workorder');
  const { closeTab, refreshTab } = useKeepAliveContext();
  const facilityContext = useSelector((state) => getFacilityContext(state));
  const role = useRole();
  const selectedFacilityId = useSelector(state => state.Facility?.selected?.Id);
  const selectedFacilityCompanyId = useSelector(
    (state) => state.Facility?.selected?.record?.company_id
  );
  // Canonical route parameters identify the record's facility context. Redux
  // remains a fallback for legacy, unscoped links.
  const facilityId = routeFacilityId || selectedFacilityId || facilityContext.facilityId;
  const companyId =
    routeCompanyId ||
    selectedFacilityCompanyId ||
    facilityContext.companyId;
  const effectiveFacilityContext = useMemo(
    () => ({ companyId, facilityId }),
    [companyId, facilityId]
  );
  const workorderListPath =
    buildFacilityPath("workorder", effectiveFacilityContext) || "/switch-view";
  // Determine if this is a create page from route
  const isCreatePage = isAdd;
  const [createPrefill, setCreatePrefill] = useState(null);
  const [createPrefillReady, setCreatePrefillReady] = useState(!isAdd);
  const sourceServiceRequestId = useMemo(() => {
    if (!isCreatePage || !createPrefillReady) return null;
    if (createPrefill) {
      return toPositiveId(
        createPrefill.service_request ?? createPrefill.service_request_id
      );
    }
    const params = new URLSearchParams(location.search);
    const explicitServiceRequestId = toPositiveId(
      params.get("service_request_id")
    );
    if (explicitServiceRequestId) return explicitServiceRequestId;

    const legacySource = params.get("source");
    return ["service-request", "tenant-request"].includes(legacySource)
      ? toPositiveId(params.get("source_id"))
      : null;
  }, [createPrefill, createPrefillReady, isCreatePage, location.search]);

  const [loading, setLoading] = useState(isCreatePage ? false : true);
  const [draftBusy, setDraftBusy] = useState(false);
  const [draftReferences, setDraftReferences] = useState({});
  const savePendingRef = useRef(false);
  const [readOnly, setReadOnly] = useState(!isCreatePage);
  const [workorderData, setWorkorderData] = useState(null);
  useEffect(() => {
    if (isCreatePage || !id) return undefined;
    const record = { companyId, facilityId, resource: "workorders", recordId: id };
    setRecordEditing(record, !readOnly);
    return () => setRecordEditing(record, false);
  }, [companyId, facilityId, id, isCreatePage, readOnly]);
  useEffect(() => {
    const refreshSavedRecord = (event) => {
      const detail = event.detail || {};
      if (!isCreatePage && readOnly && detail.resource === "workorders" &&
          String(detail.record_id) === String(id) && String(detail.companyId) === String(companyId) &&
          String(detail.facilityId) === String(facilityId)) fetchWorkorderDetail();
    };
    window.addEventListener(RECORD_SAVED_EVENT, refreshSavedRecord);
    return () => window.removeEventListener(RECORD_SAVED_EVENT, refreshSavedRecord);
  }, [companyId, facilityId, id, isCreatePage, readOnly]);
  const [equipmentOptions, setEquipmentOptions] = useState([]);
  const [serviceCategoryOptions, setServiceCategoryOptions] = useState([]);
  const [contractorDetailOpen, setContractorDetailOpen] = useState(false);
  const [userOptions, setUserOptions] = useState([]);
  const [activeTabKey, setActiveTabKey] = useState("1");
  const [unreadCommentIds, setUnreadCommentIds] = useState([]);
  const [hasComments, setHasComments] = useState(false);
  /** PM 工单检查项执行结果（与后端 pm_checklist_result 同步，可编辑） */
  const [pmChecklistResult, setPmChecklistResult] = useState(null);
  /** 当主接口未带 PM 计划 ID 或名称时，用 wrapper 详情补全 */
  const [pmPlanFromWrapper, setPmPlanFromWrapper] = useState(null);

  const userName = useSelector(state =>
    state?.Auth?.user ||
    state?.Auth?.first_name + " " + state?.Auth?.last_name
  );

  const currentUser = useSelector(state =>
    state?.Auth?.first_name + " " + state?.Auth?.last_name
  );

  const selectedProject = useSelector(state =>
    _.get(state.Project, 'selectedProject.id')
  );

  const incomingDictation = location.state?.workorderDictation;
  const dictation = sameDraftScope(incomingDictation?.scope, companyId, facilityId) ? incomingDictation : null;
  const draftEquipmentOptions = useMemo(() => [
    ...equipmentOptions,
    ...(draftReferences.equipment?.options || []).filter((option) => !equipmentOptions.some((item) => item.id === option.id)).map((option) => ({ id: option.id, name: option.label, facility: facilityId })),
  ], [equipmentOptions, draftReferences, facilityId]);
  const draftUserOptions = useMemo(() => [
    ...userOptions,
    ...(draftReferences.assignTo?.options || []).filter((option) => !userOptions.some((item) => item.id === option.id)).map((option) => ({ id: option.id, draft_label: option.label })),
  ], [userOptions, draftReferences]);
  const [priorityConfigs, setPriorityConfigs] = useState([]);
  const [costAccountOptions, setCostAccountOptions] = useState([]);
  const [sourceServiceRequest, setSourceServiceRequest] = useState(null);
  const [sourceServiceRequestError, setSourceServiceRequestError] = useState("");
  const isServiceRequestRoute = Boolean(isCreatePage && sourceServiceRequestId);
  const isContractRequestSource = Boolean(isCreatePage && createPrefill?.contract_request);
  const isServiceRequestSource = Boolean(isCreatePage && sourceServiceRequest);
  const serviceRequestSource = isCreatePage
    ? sourceServiceRequest
    : workorderData?.service_request_summary;
  const pmPlanName = !isCreatePage
    ? workorderData?.pm_plan_name || pmPlanFromWrapper?.name
    : null;
  const rawPmPlanId = !isCreatePage
    ? workorderData?.pm_plan_id ?? pmPlanFromWrapper?.id
    : null;
  const normalizedPmPlanId = Number(rawPmPlanId);
  const pmPlanId = Number.isInteger(normalizedPmPlanId) && normalizedPmPlanId > 0
    ? normalizedPmPlanId
    : null;
  const serviceRequestSourcePath = serviceRequestSource
    ? buildFacilityPath(`service-request-detail/${serviceRequestSource.id}`, effectiveFacilityContext)
    : null;
  const pmPlanSourcePath = pmPlanId
    ? buildFacilityPath(`pm-plan-detail/${pmPlanId}`, effectiveFacilityContext)
    : null;
  const workorderSource = serviceRequestSource
    ? {
        label: `Service Request ${formatServiceRequestRef(serviceRequestSource.id)} - ${serviceRequestSource.category_name}`,
        path: serviceRequestSourcePath,
      }
    : (pmPlanName || pmPlanId)
      ? {
          label: `Preventive Maintenance${pmPlanId ? ` #${pmPlanId}` : ""}${pmPlanName ? ` - ${pmPlanName}` : ""}`,
          path: pmPlanSourcePath,
        }
      : null;
  const contractRequestSource = !isCreatePage ? workorderData?.contract_summary : null;
  const contractRequestPath = contractRequestSource?.id
    ? buildFacilityPath(
        `contract-request-detail/${contractRequestSource.id}`,
        effectiveFacilityContext
      )
    : null;

  // Check if current role can edit Due Date (role 1 or 2.1)
  const canEditDueDate = role === 1 || role === 2.1;

  // Listen for new comments via WebSocket
  useEventBus(EventName.RECIVED_WORKORDER_MESSAGE, (data) => {
    if (id && id === String(data.parent_id)) {
      if (data.type === 'kpiworkordercomment_created') {
        // Only add to unread if not on Comments tab
        if (activeTabKey !== "2") {
          setUnreadCommentIds((prev) => {
            // Avoid duplicates - use comment_id if available, fallback to id
            const commentId = data.comment_id || data.id;
            if (!prev.includes(commentId)) {
              return [...prev, commentId];
            }
            return prev;
          });
        }
        // 有新消息时，设置 hasComments 为 true
        setHasComments(true);
      } else if (data.type === 'kpiworkordercomment_deleted') {
        // Use comment_id field (backend now provides this correctly)
        const commentIdToDelete = data.comment_id || data.id;

        // Remove the deleted comment ID from unread list
        setUnreadCommentIds((prev) => prev.filter(commentId => commentId !== commentIdToDelete));
      }
    }
  });

  // Mark comments as read
  const markCommentsAsRead = useCallback(() => {
    if (!id || unreadCommentIds.length === 0) return;

    api
      .updateWorkorderComment(id, { status: "read" })
      .then(() => {
        setUnreadCommentIds([]);
      })
      .catch((error) =>
        console.error("Error updating notification status:", error)
      );
  }, [id, unreadCommentIds]);

  // Handle tab change
  const handleTabChange = useCallback((key) => {
    setActiveTabKey(key);

    // If switching to Comments tab, mark as read
    if (key === "2") {
      markCommentsAsRead();
    }
  }, [markCommentsAsRead]);

  /* PM Checklist Tab：仅在建页或非 PM 类型时切回主 Tab（勿因 API 暂未返回 snapshot 而踢出） */
  useEffect(() => {
    if (activeTabKey !== "pm-checklist") return;
    if (
      isCreatePage ||
      String(watchedWorkorderType ?? workorderData?.workorder_type) !== "PM"
    ) {
      setActiveTabKey("1");
    }
  }, [
    activeTabKey,
    isCreatePage,
    workorderData?.workorder_type,
    watchedWorkorderType,
  ]);

  useEffect(() => {
    if (isCreatePage) {
      fetchEquipmentList();
      fetchServiceCategoryList();
      fetchUserList();
      // 设置新建时的默认值
      form.setFieldsValue({
        status: WORK_ORDER_INITIAL_STATUS,
        reportDate: moment(),
        reportBy: userName || undefined,
      });
        if (companyId) {
          api.getCompanyPriorityConfig(companyId).then(res => {
            if (res.status === 200 && res.data?.configs) {
              setPriorityConfigs(res.data.configs);
            }
          });
          fetchCostAccountOptions(companyId);
        }
    } else if (id) {
      fetchWorkorderDetail();
      fetchEquipmentList();
      fetchServiceCategoryList();
      fetchUserList();
    }
  }, [id, isCreatePage, companyId]);

  useEffect(() => {
    if (!isCreatePage) {
      setCreatePrefillReady(true);
      return undefined;
    }
    if (dictation) {
      clearFlowPrefill(WORKORDER_CREATE_PREFILL_KEY);
      setCreatePrefill(null);
      setCreatePrefillReady(true);
      return undefined;
    }
    const prefill = readAndClearFlowPrefill(WORKORDER_CREATE_PREFILL_KEY);
    if (prefill) {
      setCreatePrefill(prefill);
      form.setFieldsValue({
        ...prefill,
        due_date: prefill.due_date ? moment(prefill.due_date) : null,
        complete_date: prefill.complete_date ? moment(prefill.complete_date) : null,
        reportDate: prefill.reportDate ? moment(prefill.reportDate) : moment(),
      });
    }
    setCreatePrefillReady(true);
    return undefined;
  }, [form, isCreatePage, dictation]);

  useEffect(() => () => clearFlowPrefill(WORKORDER_CREATE_PREFILL_KEY), []);

  useEffect(() => {
    if (
      !isCreatePage ||
      !createPrefillReady ||
      !sourceServiceRequestId ||
      isContractRequestSource
    ) {
      setSourceServiceRequest(null);
      setSourceServiceRequestError("");
      return undefined;
    }
    let active = true;
    setSourceServiceRequestError("");
    api.getFacilityServiceRequest(sourceServiceRequestId)
      .then((response) => {
        const source = response.data;
        if (!active) return;
        if (
          source.status === "closed" ||
          source.status === "converted" ||
          source.has_workorder ||
          !["not_required", "approved"].includes(source.cost_approval_status)
        ) {
          const errorMessage = "This service request is not eligible to create a work order";
          globalMessage.error(errorMessage);
          setSourceServiceRequest(null);
          setSourceServiceRequestError(errorMessage);
          return;
        }
        setSourceServiceRequest(source);
        form.setFieldsValue({
          issue: source.title,
          description: source.description,
          priority: source.priority,
          service_category: source.category,
          estimate_budget:
            source.cost_approval_status === "approved" ? source.quoted_cost : undefined,
        });
      })
      .catch(() => {
        if (active) {
          setSourceServiceRequest(null);
          const errorMessage = "Failed to load source service request";
          setSourceServiceRequestError(errorMessage);
          globalMessage.error(errorMessage);
        }
      });
    return () => {
      active = false;
    };
  }, [
    createPrefillReady,
    form,
    isContractRequestSource,
    isCreatePage,
    sourceServiceRequestId,
  ]);

  const fetchWorkorderDetail = async () => {
    try {
      setLoading(true);
      const response = await api.getWorkorderById(id);

      if (response.status === 200 && response.data && response.data.id) {
        const data = response.data;
        setWorkorderData(data);
        setPmPlanFromWrapper(null);
        setPmChecklistResult(
          data.pm_checklist_result != null
            ? ensurePmChecklistShape(data.pm_checklist_result)
            : null
        );

        // Fetch comments to get unread comment IDs
        try {
          const commentsResponse = await api.getWorkorderComment(id);
          if (commentsResponse.status === 200 && commentsResponse.data.results) {
            const unreadIds = commentsResponse.data.results
              .filter(comment => comment.status === 'unread')
              .map(comment => comment.id);
            setUnreadCommentIds(unreadIds);

            // 检查是否有 comments 数据
            const hasCommentsData = commentsResponse.data.results && commentsResponse.data.results.length > 0;
            setHasComments(hasCommentsData);
          }
        } catch (error) {
          console.error("Error fetching comments:", error);
          // Fallback: just set empty array
          setUnreadCommentIds([]);
          setHasComments(false);
        }

        // Set form values with date conversion（后端 DateField 需 YYYY-MM-DD；API 可能返回 ISO 时间串）
        const formValues = {
          ...data,
          complete_date: data.complete_date ? moment(data.complete_date) : null,
          reportDate: data.reportDate ? moment(data.reportDate) : null,
          due_date: data.due_date ? moment(data.due_date) : null,
          created_date: data.created_date ? moment(data.created_date).format("YYYY-MM-DD HH:mm") : "",
          modified_date: data.modified_date ? moment(data.modified_date).format("YYYY-MM-DD HH:mm") : "",
        };
        form.setFieldsValue(formValues);
      } else {
        globalMessage.error("Work order not found");
      }
    } catch (error) {
      console.error("Error fetching workorder:", error);
      globalMessage.error("Failed to load work order details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isCreatePage || !workorderData) return;
    let cancelled = false;
    (async () => {
      const cid = await resolveCompanyIdForWorkorder(workorderData, null);
      if (!cancelled && cid) await fetchCostAccountOptions(cid);
    })();
    return () => {
      cancelled = true;
    };
  }, [isCreatePage, workorderData, equipmentOptions, companyId]);

  useEffect(() => {
    if (isCreatePage || !workorderData) {
      setPmPlanFromWrapper(null);
      return;
    }
    if (workorderData.pm_plan_id != null && workorderData.pm_plan_name) {
      setPmPlanFromWrapper(null);
      return;
    }
    const wid = workorderData.workorder_wrapper;
    if (wid == null) {
      setPmPlanFromWrapper(null);
      return;
    }
    setPmPlanFromWrapper(null);
    let cancelled = false;
    (async () => {
      try {
        const r = await api.getWorkorderWrapperDetail(wid);
        if (cancelled || r.status !== 200 || !r.data) return;
        const plan = {
          id: r.data.pm_plan ?? null,
          name: r.data.pm_plan_name || null,
        };
        if (plan.id != null || plan.name) setPmPlanFromWrapper(plan);
      } catch (e) {
        if (!cancelled) setPmPlanFromWrapper(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isCreatePage, workorderData?.id, workorderData?.workorder_wrapper, workorderData?.pm_plan_id, workorderData?.pm_plan_name]);

  const fetchEquipmentList = async () => {
    try {
      const response = await api.filterEquipment("page=1&page_size=999");
      if (response.status === 200) {
        setEquipmentOptions(response.data.results || []);
      }
    } catch (error) {
      console.error("Error fetching equipment list:", error);
    }
  };

  const fetchServiceCategoryList = async () => {
    try {
      const response = await api.getServiceCategories();
      if (response.status === 200) {
        const payload = response.data;
        setServiceCategoryOptions(Array.isArray(payload) ? payload : payload?.results || []);
      }
    } catch (error) {
      console.error("Error fetching service category list:", error);
    }
  };

  const fetchCostAccountOptions = async (cid) => {
    if (!cid) {
      setCostAccountOptions([]);
      return;
    }
    try {
      const res = await api.getCostAccounts(cid);
      if (res.status === 200 && Array.isArray(res.data)) {
        setCostAccountOptions(res.data);
      } else {
        setCostAccountOptions([]);
      }
    } catch (e) {
      console.error("Error loading cost accounts:", e);
      setCostAccountOptions([]);
    }
  };

  const resolveCompanyIdForWorkorder = async (data, equipmentIdOverride) => {
    if (companyId) return companyId;
    if (data?.facility) {
      try {
        const r = await api.filterFacility(`id=${data.facility}`);
        if (r.status === 200 && r.data.results?.[0]?.company != null) {
          return r.data.results[0].company;
        }
      } catch (e) {
        console.error(e);
      }
    }
    // 多选语义：成本归属取第一台关联设备
    const eqId = equipmentIdOverride ?? data?.equipments?.[0];
    if (eqId && equipmentOptions.length) {
      const eq = equipmentOptions.find((e) => e.id === eqId);
      if (eq?.facility) {
        try {
          const r = await api.filterFacility(`id=${eq.facility}`);
          if (r.status === 200 && r.data.results?.[0]?.company != null) {
            return r.data.results[0].company;
          }
        } catch (e) {
          console.error(e);
        }
      }
    }
    return null;
  };

  const fetchUserList = async () => {
    try {
      const response = await api.filterAssignedUsers(facilityId);
      if (response.status === 200) {
        let users = response.data.results || [];
        // 根据当前用户角色过滤用户列表
        if (role === 1) {
          // 角色 1.0: 返回所有数据
          setUserOptions(users);
        } else if (role === 2.1) {
          // 角色 2.1: 返回用户角色中不包含 1.0 的用户
          const filteredUsers = users.filter(user => user.role !== 1);
          setUserOptions(filteredUsers);
        } else if (role === 2.2 || role === 4) {
          // 角色 2.2 或 3: 只返回角色是 3 的用户
          const filteredUsers = users.filter(user => user.role === 4);
          setUserOptions(filteredUsers);
        } else if(role === 3) {
          const filteredUsers = users.filter(user => user.role === 4 || user.role === 3);
          setUserOptions(filteredUsers);
        }
      }
    } catch (error) {
      console.error("Error fetching user list:", error);
    }
  };

  const handleEdit = () => {
    setReadOnly(false);
  };

  const handleCancel = () => {
    setReadOnly(true);
    if (workorderData) {
      setPmChecklistResult(
        workorderData.pm_checklist_result != null
          ? ensurePmChecklistShape(workorderData.pm_checklist_result)
          : null
      );
      const formValues = {
        ...workorderData,
        complete_date: workorderData.complete_date ? moment(workorderData.complete_date) : null,
        reportDate: workorderData.reportDate ? moment(workorderData.reportDate) : null,
        due_date: workorderData.due_date ? moment(workorderData.due_date) : null,
        created_date: workorderData.created_date ? moment(workorderData.created_date).format("YYYY-MM-DD HH:mm") : "",
        modified_date: workorderData.modified_date ? moment(workorderData.modified_date).format("YYYY-MM-DD HH:mm") : "",
      };
      form.setFieldsValue(formValues);
    }
  };

  const updatePmChecklistItemValue = useCallback((index, value) => {
    setPmChecklistResult((prev) => {
      const shaped = ensurePmChecklistShape(prev);
      if (!shaped?.items?.[index]) return prev;
      const next = JSON.parse(JSON.stringify(shaped));
      next.items[index] = { ...next.items[index], value };
      return next;
    });
  }, []);

  const updatePmChecklistMatrixCell = useCallback((rowIndex, colId, value) => {
    setPmChecklistResult((prev) => {
      const shaped = ensurePmChecklistShape(prev);
      if (!shaped?.items?.[rowIndex] || colId == null) return prev;
      const next = JSON.parse(JSON.stringify(shaped));
      next.items[rowIndex] = { ...next.items[rowIndex], [colId]: value };
      return next;
    });
  }, []);

  const pmChecklistView = useMemo(
    () => getPmChecklistViewModel(pmChecklistResult),
    [pmChecklistResult]
  );

  const renderPmChecklistMatrixTable = useCallback(
    (view) => {
      const { items, columns: tmplCols } = view;
      const fieldDisabled = readOnly || !editable;
      const colW = 160;

      const renderCellInput = (col, record, rowIndex) => {
        const cid = col.id;
        const val = record[cid];
        const t = (col.type || "text").toLowerCase();

        if (t === "label") {
          return <span style={{ color: "#8c8c8c" }}>—</span>;
        }
        if (t === "na") {
          return (
            <span style={{ color: "#94a3b8", fontWeight: 600 }} title="Not applicable">
              N/A
            </span>
          );
        }
        if (fieldDisabled) {
          if (t === "boolean") {
            return (
              <span>
                {val === true || val === "true"
                  ? "Yes"
                  : val === false || val === "false"
                    ? "No"
                    : ""}
              </span>
            );
          }
          return <span>{safePmCellDisplay(val)}</span>;
        }
        if (t === "select") {
          const opts = normalizePmChecklistOptions(col.options ?? col.description);
          return (
            <Select
              allowClear
              style={{ width: "100%" }}
              placeholder="Select"
              value={val === "" ? undefined : val}
              onChange={(v) => updatePmChecklistMatrixCell(rowIndex, cid, v)}
            >
              {opts.map((opt) => (
                <Option key={String(opt)} value={opt}>
                  {String(opt)}
                </Option>
              ))}
            </Select>
          );
        }
        if (t === "number") {
          return (
            <InputNumber
              style={{ width: "100%" }}
              value={val === "" || val == null ? null : Number(val)}
              onChange={(v) => updatePmChecklistMatrixCell(rowIndex, cid, v)}
            />
          );
        }
        if (t === "boolean") {
          return (
            <Checkbox
              checked={val === true || val === "true"}
              onChange={(e) => updatePmChecklistMatrixCell(rowIndex, cid, e.target.checked)}
            />
          );
        }
        if (t === "textarea") {
          return (
            <TextArea
              rows={2}
              value={val != null ? String(val) : ""}
              onChange={(e) => updatePmChecklistMatrixCell(rowIndex, cid, e.target.value)}
            />
          );
        }
        if (t === "date") {
          return (
            <DatePicker
              style={{ width: "100%" }}
              value={val ? moment(val) : null}
              onChange={(d) =>
                updatePmChecklistMatrixCell(rowIndex, cid, d ? d.format("YYYY-MM-DD") : null)
              }
            />
          );
        }
        return (
          <Input
            value={val != null ? val : ""}
            onChange={(e) => updatePmChecklistMatrixCell(rowIndex, cid, e.target.value)}
          />
        );
      };

      const tableColumns = [
        {
          title: "Item",
          key: "rowLabel",
          width: 200,
          fixed: "left",
          render: (_, record) => (
            <span style={{ fontWeight: 600 }}>{record.label ?? record.name ?? "—"}</span>
          ),
        },
        ...tmplCols.map((col) => ({
          title: col.label ?? col.id,
          key: col.id,
          width: col.type === "boolean" ? 100 : colW,
          render: (_, record) => renderCellInput(col, record, record._rowIndex),
        })),
      ];

      const scrollX = Math.max(720, 200 + tmplCols.length * colW);

      return (
        <Table
          dataSource={items.map((r, i) => ({ ...r, _rowIndex: i }))}
          rowKey={(r) => String(r.id ?? r._rowIndex)}
          pagination={false}
          scroll={{ x: scrollX }}
          columns={tableColumns}
        />
      );
    },
    [readOnly, editable, updatePmChecklistMatrixCell]
  );
  const renderPmChecklistItem = useCallback(
    (item, index) => {
      const label = item?.name != null ? String(item.name) : `Item ${index + 1}`;
      const t = (item?.type || "text").toLowerCase();
      const fieldDisabled = readOnly || !editable;
      const unitStr = formatPmChecklistUnit(item.unit);
      const isRequired = Boolean(item.required);
      const showUnitBelowLabel = Boolean(unitStr && (t === "textarea" || t === "select"));

      const labelBlock = (
        <div>
          <label
            style={{
              display: "block",
              fontWeight: 600,
              color: "rgba(0,0,0,0.88)",
              fontSize: 14,
            }}
          >
            {isRequired ? <span style={{ color: "#ff4d4f", marginRight: 4 }}>*</span> : null}
            {label}
          </label>
          {showUnitBelowLabel ? (
            <div style={{ fontSize: 12, color: "#8c8c8c", marginTop: 6 }}>
              Unit: <span style={{ color: "#595959", fontWeight: 500 }}>{unitStr}</span>
            </div>
          ) : null}
        </div>
      );

      const wrapCard = (mainControl) => {
        return (
          <Col xs={24} lg={24} key={`pm-checklist-${index}`}>
            <div style={PM_CHECKLIST_FIELD_CARD_STYLE}>
              <Row gutter={[0, 10]}>
                <Col span={24}>{labelBlock}</Col>
                <Col span={24}>
                  <div style={{ width: "100%" }}>{mainControl}</div>
                </Col>
              </Row>
            </div>
          </Col>
        );
      };

      if (t === "select") {
        const opts = normalizePmChecklistOptions(item.options ?? item.description);
        return wrapCard(
          <Select
            allowClear
            disabled={fieldDisabled}
            placeholder="Select"
            style={{ width: "100%" }}
            value={item.value}
            onChange={(v) => updatePmChecklistItemValue(index, v)}
          >
            {opts.map((opt) => (
              <Option key={String(opt)} value={opt}>
                {String(opt)}
              </Option>
            ))}
          </Select>
        );
      }

      if (t === "number") {
        return wrapCard(
          <Input
            type="number"
            disabled={fieldDisabled}
            style={{ width: "100%" }}
            value={
              item.value === null || item.value === undefined || item.value === ""
                ? ""
                : item.value
            }
            placeholder={unitStr ? `Numeric value (${unitStr})` : "Enter number"}
            addonAfter={
              unitStr ? (
                <span
                  style={{
                    minWidth: 40,
                    display: "inline-block",
                    textAlign: "center",
                    color: "rgba(0,0,0,0.65)",
                    fontWeight: 500,
                  }}
                >
                  {unitStr}
                </span>
              ) : undefined
            }
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === "" || raw === null) {
                updatePmChecklistItemValue(index, null);
                return;
              }
              const n = parseFloat(raw);
              updatePmChecklistItemValue(index, Number.isNaN(n) ? raw : n);
            }}
          />
        );
      }

      if (t === "boolean") {
        const boolVal =
          item.value === true || item.value === "true"
            ? "true"
            : item.value === false || item.value === "false"
              ? "false"
              : undefined;
        return wrapCard(
          <Select
            allowClear
            disabled={fieldDisabled}
            placeholder="Select"
            style={{ width: "100%" }}
            value={boolVal}
            onChange={(v) => {
              if (v === "true") updatePmChecklistItemValue(index, true);
              else if (v === "false") updatePmChecklistItemValue(index, false);
              else updatePmChecklistItemValue(index, null);
            }}
          >
            <Option value="true">Yes</Option>
            <Option value="false">No</Option>
          </Select>
        );
      }

      if (t === "textarea") {
        return wrapCard(
          <TextArea
            rows={3}
            disabled={fieldDisabled}
            placeholder="Enter notes"
            value={item.value != null ? String(item.value) : ""}
            onChange={(e) => updatePmChecklistItemValue(index, e.target.value)}
          />
        );
      }

      return wrapCard(
        <Input
          disabled={fieldDisabled}
          placeholder="Enter value"
          suffix={
            unitStr ? (
              <span style={{ color: "#8c8c8c", userSelect: "none", fontSize: 13 }}>{unitStr}</span>
            ) : null
          }
          value={item.value != null ? item.value : ""}
          onChange={(e) => updatePmChecklistItemValue(index, e.target.value)}
        />
      );
    },
    [readOnly, editable, updatePmChecklistItemValue]
  );

  // Handle Form Values Change (Status & Priority)
  const handleValuesChange = (changedValues, allValues) => {
    // 1. Handle Status change to manage Completed Date
    if (changedValues.status !== undefined) {
      const newStatus = changedValues.status;

      if (WORK_ORDER_TERMINAL_STATUSES.includes(newStatus)) {
        // If status is Completed and complete_date is empty, set it to today
        if (!allValues.complete_date) {
          form.setFieldsValue({
            complete_date: moment()
          });
        }
      } else {
        // If status is not Completed, clear complete_date
        form.setFieldsValue({
          complete_date: null
        });
      }
    }

    // 2b. Equipment change (create): resolve company → load cost account options
    // 多选语义：取第一台关联设备
    if (changedValues.equipments !== undefined && isCreatePage) {
      const eqId = changedValues.equipments?.[0];
      if (eqId && equipmentOptions.length) {
        const eq = equipmentOptions.find((e) => e.id === eqId);
        if (eq?.facility) {
          api.filterFacility(`id=${eq.facility}`).then((r) => {
            const cid = r.data.results?.[0]?.company;
            if (cid) fetchCostAccountOptions(cid);
          });
        }
      } else if (!eqId) {
        setCostAccountOptions([]);
      }
    }

    // 2. Handle Priority change to auto-calculate Due Date (Create mode only)
    if (changedValues.priority !== undefined && isCreatePage) {
      const newPriority = changedValues.priority;

      // Calculate due_date regardless of current value if configs exist
      if (priorityConfigs.length > 0) {
        const config = priorityConfigs.find(c => c.priority === newPriority);
        if (config && config.delay_day >= 0) {
          const newDueDate = moment().add(config.delay_day, 'days');
          form.setFieldsValue({
            due_date: newDueDate
          });
        }
      }
    }
  };

  const saveWorkorder = async (values) => {
    if (savePendingRef.current || draftBusy) return;
    savePendingRef.current = true;
    try {
      setLoading(true);

      const toDateOnly = (v) => {
        if (!v) return null;
        const m = moment.isMoment(v) ? v : moment(v);
        return m.isValid() ? m.format("YYYY-MM-DD") : null;
      };

      // Format dates before submitting（DRF DateField 只接受 YYYY-MM-DD）
      const submitValues = {
        ...values,
        complete_date: values.complete_date ? values.complete_date.format("YYYY-MM-DD") : null,
        reportDate: values.reportDate ? values.reportDate.format("YYYY-MM-DD") : null,
        due_date: values.due_date ? values.due_date.format("YYYY-MM-DD") : null,
      };
      // contractor 由后端按关联合同请求的 Awarded Contractor 派生，禁止通过工单接口提交
      delete submitValues.contractor;

      if (
        !isCreatePage &&
        pmChecklistResult &&
        String(values.workorder_type ?? "") === "PM"
      ) {
        submitValues.pm_checklist_result = pmChecklistResult;
      }

      let response;
      if (isCreatePage) {
        [
          "source",
          "source_id",
          "service_request_id",
          "service_request_category_id",
          "sourceLabel",
        ].forEach((field) => delete submitValues[field]);
        // Create mode - set project if available
        if (selectedProject) {
          submitValues.project = selectedProject;
        }
        if (isContractRequestSource) {
          submitValues.contract_request = createPrefill.contract_request;
          submitValues.facility = createPrefill.facility || facilityId;
          const contractServiceRequestId = toPositiveId(
            createPrefill.service_request ?? createPrefill.service_request_id
          );
          if (contractServiceRequestId) {
            submitValues.service_request = contractServiceRequestId;
          } else {
            delete submitValues.service_request;
          }
        }
        if (isServiceRequestSource) {
          submitValues.service_request = sourceServiceRequest.id;
        }
        response = await api.createWorkorder(submitValues);
      } else {
        // Edit mode
        submitValues.modifiedby_name = userName;
        response = await api.editWorkorder(id, submitValues);
      }

      if (response.status === 200 || response.status === 201) {
        globalMessage.success({
          message: "Success",
          description: isCreatePage ? "Work order created successfully" : "Work order updated successfully",
        });
        clearFlowPrefill(WORKORDER_CREATE_PREFILL_KEY);

        // Emit event to refresh workorder list
        emitEvent(EventName.REFRESH_WORKORDER_LIST);

        if (isCreatePage) {
          // Refresh Workorder list page and close current tab
          refreshTab(workorderListPath);
          closeTab();
          if ((isServiceRequestSource || isContractRequestSource || dictation || Object.keys(draftReferences).length > 0) && response.data?.id) {
            const detailPath =
              buildFacilityPath(
                `workorder-detail/${response.data.id}`,
                effectiveFacilityContext
              ) ||
              workorderListPath;
            navigate(detailPath);
          } else {
            navigate(workorderListPath);
          }
        } else {
          setReadOnly(true);
          fetchWorkorderDetail();
        }
      } else {
        globalMessage.error({
          message: "Error",
          description: isCreatePage ? "Failed to create work order" : "Failed to update work order",
        });
      }
    } catch (error) {
      console.error(`Error ${isCreatePage ? 'creating' : 'updating'} workorder:`, error);
      globalMessage.error({
        message: "Error",
        description: isCreatePage ? "Failed to create work order" : "Failed to update work order",
      });
    } finally {
      savePendingRef.current = false;
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    if (isCreatePage && !createPrefillReady) return;
    if (isServiceRequestRoute && !isServiceRequestSource && !isContractRequestSource) {
      globalMessage.error(
        sourceServiceRequestError || "Source service request is unavailable"
      );
      return;
    }
    if (isServiceRequestSource && (!values.equipments || values.equipments.length === 0)) {
      Modal.confirm({
        title: "Equipment is not selected",
        content: "Save this service-request work order without Equipment?",
        okText: "Save without Equipment",
        cancelText: "Continue Editing",
        onOk: () => saveWorkorder(values),
      });
      return;
    }
    return saveWorkorder(values);
  };

  const handleDelete = () => {
    Modal.confirm({
      title: "Are you sure you want to delete this work order?",
      content: "This action cannot be undone.",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          setLoading(true);
          const response = await api.deleteWorkorder(id);

          if (response.status === 204 || response.status === 200) {
            globalMessage.success({
              message: "Success",
              description: "Work order deleted successfully",
            });
            // Refresh Workorder list page and close current tab
            refreshTab(workorderListPath);
            closeTab();
            navigate(workorderListPath);
          } else {
            globalMessage.error({
              message: "Error",
              description: "Failed to delete work order",
            });
          }
        } catch (error) {
          console.error("Error deleting workorder:", error);
          globalMessage.error({
            message: "Error",
            description: "Failed to delete work order",
          });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleConvertToContract = () => {
    const contractPath = buildFacilityPath(
      "create-contract-request",
      effectiveFacilityContext
    );
    if (!contractPath) return;
    if (!workorderData) return;
    const saved = writeFlowPrefill(CONTRACT_CREATE_PREFILL_KEY, {
      source: "workorder",
      source_id: workorderData.id,
      sourceLabel: `Work Order ${formatWorkorderRef(workorderData.workordernumber ?? id)}`,
      workorder: workorderData.id,
      service_request: workorderData.service_request || null,
      service_request_id: workorderData.service_request || null,
      facility: workorderData.facility || facilityId,
      equipments: workorderData.equipments || [],
      service_category: workorderData.service_category || null,
      title: workorderData.issue || "",
      description: workorderData.description || "",
      priority: workorderData.priority || null,
      due_date: workorderData.due_date || null,
      estimate_budget: workorderData.estimate_budget ?? null,
      contractor: workorderData.contractor || null,
      contractor_ids: workorderData.contractor
        ? [workorderData.contractor]
        : undefined,
    });
    if (!saved) {
      globalMessage.error("Unable to prepare the contract request form. Please try again.");
      return;
    }
    navigate(contractPath);
  };

  const handleCancelCreate = () => {
    clearFlowPrefill(WORKORDER_CREATE_PREFILL_KEY);
    closeTab();
    navigate(workorderListPath);
  };

  const mergedCostAccountOptions = useMemo(() => {
    const list = [...costAccountOptions];
    const codes = new Set(list.map((r) => r.code));
    const cur = !isCreatePage && workorderData?.cost_account;
    if (cur && !codes.has(cur)) {
      list.push({
        code: cur,
        label: `${cur} (legacy)`,
        is_builtin: false,
      });
    }
    return list;
  }, [costAccountOptions, workorderData, isCreatePage]);

  /**
   * @param {{ hideWorkorderDelete?: boolean }} opts
   * hideWorkorderDelete: on PM Checklist tab, hide Delete — it removes the whole work order and is
   * easily mistaken for deleting plan-defined checklist rows (checklist structure is not editable here).
   */
  const renderOperationButton = (opts = {}) => {
    const { hideWorkorderDelete = false } = opts;
    if (isCreatePage) {
      return (
        <>
          <Button onClick={handleCancelCreate}>
            <i className="fa-solid fa-xmark"></i>Cancel
          </Button>
          <Button type="primary" loading={loading} disabled={draftBusy || !editable} onClick={() => form.submit()}>
            <i className="fa-solid fa-floppy-disk"></i>Save
          </Button>
        </>
      );
    }

    if (readOnly) {
      return (
        <>
          {editable && <Button type="primary" onClick={handleEdit}>
            <i className="fa-solid fa-pen-to-square"></i>Edit
          </Button>}
          {editable && contractEditable && !workorderData?.contract_summary && (
            <Button onClick={handleConvertToContract}>
              <i className="fa-solid fa-file-signature"></i>Convert to Contract
            </Button>
          )}
          {fullAccess && !hideWorkorderDelete && <Button danger onClick={handleDelete}>
            <i className="fa-solid fa-trash"></i>Delete
          </Button>}
        </>
      );
    } else {
      return (
        <>
          <Button type="primary" onClick={() => form.submit()}>
            <i className="fa-solid fa-floppy-disk"></i>Save
          </Button>
          <Button onClick={handleCancel}>
            Cancel
          </Button>
        </>
      );
    }
  };

  if (loading && !workorderData && !isCreatePage) {
    return (
      <div style={{ textAlign: "center", padding: "100px 0" }}>
        <Spin size="large" />
      </div>
    );
  }

  const showPmChecklistTab =
    !isCreatePage &&
    String(watchedWorkorderType ?? workorderData?.workorder_type) === "PM";

  return (
    <div className="workorder-detail-container">
      {!isCreatePage && <AiContextAction resource="workorders" recordId={workorderData?.id} disabled={loading || String(workorderData?.id) !== String(id)} />}
      <Spin spinning={loading || (isCreatePage && !createPrefillReady)}>
        <DetailTabs
          defaultActiveKey="1" 
          /* false：在 PM Checklist Tab 点 Save 时 Detail 表单仍需挂载以 validateFields/submit */
          destroyInactiveTabPane={false}
          activeKey={activeTabKey}
          onChange={handleTabChange}
          tabBarExtraContent={
            activeTabKey === "1" || activeTabKey === "pm-checklist"
              ? renderOperationButton({
                  hideWorkorderDelete: activeTabKey === "pm-checklist",
                })
              : null
          }
        >
          <TabPane
            tab={
              <span>
                <i className="fa-regular fa-rectangle-list pr-2"></i>Detail
              </span>
            }
            key="1"
          >
            <div>
              <div className="slds-m-around_x-small">
                <div className="px-4">
                  <div className="w-full">
                    {isCreatePage && createPrefillReady && editable && canUseAi && !isServiceRequestRoute && !isContractRequestSource && (
                      <WorkorderDraftPanel
                        key={`${companyId}:${facilityId}:${dictation?.id || "manual"}`}
                        companyId={companyId}
                        facilityId={facilityId}
                        form={form}
                        initialText={dictation?.text || ""}
                        autoPrepare={Boolean(dictation)}
                        onBusyChange={setDraftBusy}
                        onApplied={(values, references) => {
                          setDraftReferences(references || {});
                          const changes = { ...values };
                          if (form.getFieldValue("due_date")) delete changes.priority;
                          handleValuesChange(changes, form.getFieldsValue());
                        }}
                      />
                    )}
                    <Form
                      form={form}
                      onFinish={handleSubmit}
                      onValuesChange={handleValuesChange}
                      layout="horizontal"
                      labelCol={{ span: 8 }}
                      wrapperCol={{ span: 16 }}
                      className="detailForm workorder-detail-form"
                        autoComplete="off">
                      <Row gutter={[0, 0]}>
                        <Col xs={24} lg={12}>
                          <Form.Item label="ID" name="id">
                            <Input disabled />
                          </Form.Item>
                        </Col>
                        <Col xs={0} lg={12} />

                        <Col xs={24} lg={12}>
                          <Form.Item
                            label="WorkOrder Number"
                            name="workordernumber"
                          >
                            <Input disabled={readOnly} />
                          </Form.Item>
                        </Col>

                        <Col xs={24} lg={24} style={{ padding: 0 }} className="workorder-detail-main-section">
                          <Row align="stretch" gutter={[0, 0]} className="workorder-detail-main-row">
                            <Col xs={24} lg={12}>
                          <Form.Item
                            label="Equipment"
                            name="equipments"
                            rules={
                              isServiceRequestRoute || isContractRequestSource
                                ? []
                                : [{ required: true, message: "This field is required" }]
                            }
                          >
                            <Select
                              mode="multiple"
                              showSearch
                              placeholder="Select equipment"
                              optionFilterProp="children"
                              filterOption={(input, option) =>
                                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                              }
                              disabled={readOnly}
                              maxTagCount="responsive"
                            >
                              {draftEquipmentOptions.map(item => (
                                <Option key={item.id} value={item.id}>
                                  {item.name}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>

                          <Form.Item
                            label="Type"
                            name="workorder_type"
                            rules={[{ required: true, message: "This field is required" }]}
                          >
                            <Select disabled={readOnly} placeholder="Please select">
                              {workOrderTypeValues.map((value) => (
                                <Option key={value} value={value}>
                                  {formatWorkOrderDomainValue(
                                    "workorder_type",
                                    value,
                                    semanticCapabilities
                                  )}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                          <Form.Item label="Service Category" name="service_category">
                            <Select
                              allowClear
                              showSearch
                              optionFilterProp="children"
                              disabled={readOnly}
                              placeholder="Select service category"
                            >
                              {serviceCategoryOptions.map((item) => (
                                <Option key={item.id} value={item.id}>
                                  {item.name}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                          {workorderSource && (
                            <Form.Item label="Source">
                              {!isCreatePage && workorderSource.path ? (
                                <Button
                                  type="link"
                                  className="workorder-source-link"
                                  onClick={() => navigate(workorderSource.path)}
                                >
                                  {workorderSource.label}
                                </Button>
                              ) : (
                                <AntText type="secondary">
                                  {workorderSource.label}
                                </AntText>
                              )}
                            </Form.Item>
                          )}

                          {contractRequestSource && (
                            <Form.Item label="Contract Request">
                              {contractRequestPath ? (
                                <Button
                                  type="link"
                                  className="workorder-source-link"
                                  onClick={() => navigate(contractRequestPath)}
                                >
                                  {contractRequestSource.title || `Contract Request ${formatContractRef(contractRequestSource.id)}`}
                                </Button>
                              ) : (
                                <AntText type="secondary">
                                  {contractRequestSource.title || `Contract Request ${formatContractRef(contractRequestSource.id)}`}
                                </AntText>
                              )}
                            </Form.Item>
                          )}

                          {(() => {
                            // contractor 只读：由关联合同请求的 Awarded Contractor 后端派生，任何模式不可编辑
                            const contractorId = workorderData?.contractor ?? createPrefill?.contractor ?? null;
                            if (contractorId == null) return null;
                            const contractorLabel =
                              workorderData?.contractor_name ||
                              createPrefill?.contractor_name ||
                              `Contractor #${contractorId}`;
                            return (
                              <Form.Item label="Assign To contractor">
                                {isCreatePage ? (
                                  <AntText>{contractorLabel}</AntText>
                                ) : (
                                  <a
                                    href="#contractor-details"
                                    onClick={(event) => {
                                      event.preventDefault();
                                      setContractorDetailOpen(true);
                                    }}
                                    style={{ color: "#1677ff", textDecoration: "underline" }}
                                  >
                                    {contractorLabel}
                                  </a>
                                )}
                              </Form.Item>
                            );
                          })()}

                          <Form.Item
                            label="Reported By"
                            name="reportBy"
                            rules={[{ required: true, message: "This field is required" }]}
                          >
                            <Input disabled={readOnly} />
                          </Form.Item>

                          <Form.Item label="Report Date" name="reportDate">
                            <DatePicker format="YYYY-MM-DD" style={{ width: "100%" }} disabled={readOnly} />
                          </Form.Item>

                          <Form.Item label="Work Due Date" name="due_date">
                            <DatePicker
                              format="YYYY-MM-DD"
                              style={{ width: "100%" }}
                              disabled={readOnly || !canEditDueDate}
                            />
                          </Form.Item>

                          <Form.Item
                            label="Priority"
                            name="priority"
                            rules={[{ required: true, message: "This field is required" }]}
                          >
                            <Select disabled={readOnly}>
                              {workOrderPriorityValues.map((value) => (
                                <Option key={value} value={value}>{value}</Option>
                              ))}
                            </Select>
                          </Form.Item>

                          <Form.Item label="Energy Impact" name="energy_impact">
                            <Select disabled={readOnly} placeholder="Please select">
                              {workOrderEnergyImpactValues.map((value) => (
                                <Option key={value} value={value}>{value}</Option>
                              ))}
                            </Select>
                          </Form.Item>

                          <Form.Item
                            label="Status"
                            name="status"
                            rules={[{ required: true, message: "This field is required" }]}
                          >
                            <Select disabled={readOnly}>
                              {WORK_ORDER_STATUS_VALUES.map((status) => (
                                <Option key={status} value={status}>
                                  {formatWorkOrderStatus(status)}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>

                          <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.status !== currentValues.status}>
                            {({ getFieldValue }) => {
                              const currentStatus = getFieldValue('status');
                              const isDisabled = readOnly || !WORK_ORDER_TERMINAL_STATUSES.includes(currentStatus);

                              return (
                                <Form.Item label="Completed Date" name="complete_date">
                                  <DatePicker
                                    format="YYYY-MM-DD"
                                    style={{ width: "100%" }}
                                    disabled={isDisabled}
                                  />
                                </Form.Item>
                              );
                            }}
                          </Form.Item>
                            </Col>

                            <Col xs={24} lg={12}>
                          <Form.Item
                            label="Issue"
                            name="issue"
                            rules={[{ required: true, message: "This field is required" }]}
                          >
                            <TextArea rows={3} disabled={readOnly} />
                          </Form.Item>

                          <Form.Item label="Description" name="description">
                            <TextArea rows={6} disabled={readOnly} />
                          </Form.Item>

                          <Form.Item label="Recommended Action" name="recommended_action">
                            <TextArea rows={3} disabled={readOnly} />
                          </Form.Item>

                          <Form.Item label="Responsible Party" name="responsible_party">
                            <Input disabled={readOnly} />
                          </Form.Item>

                          <Form.Item label="Assign To" name="assignTo">
                            <Select
                              showSearch
                              placeholder="Select user"
                              optionFilterProp="label"
                              disabled={readOnly}
                            >
                              {draftUserOptions.map(user => (
                                <Option
                                  key={user.id}
                                  value={user.id}
                                  label={user.draft_label || `${user.username}（${user.email}）`}
                                >
                                  {user.draft_label || `${user.username}（${user.email}）`}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                            </Col>
                          </Row>
                        </Col>

                        {/* v2.0: Cost Fields */}
                        <Col xs={24} lg={24} className="workorder-cost-section">
                          <Divider orientation="left" className="workorder-cost-section-divider">
                            <i className="fa-solid fa-dollar-sign" style={{ marginRight: 6 }} />
                            Cost Details
                          </Divider>
                        </Col>

                        <Col xs={24} lg={12}>
                          <Form.Item label="Est. Hours" name="estimate_hours">
                            <Input type="number" min={0} disabled={readOnly} addonAfter="hrs" />
                          </Form.Item>
                        </Col>
                        <Col xs={24} lg={12}>
                          <Form.Item label="Actual Hours" name="actual_hours">
                            <Input type="number" min={0} disabled={readOnly} addonAfter="hrs" />
                          </Form.Item>
                        </Col>
                        <Col xs={24} lg={12}>
                          <Form.Item label="Est. Budget" name="estimate_budget">
                            <Input type="number" min={0} step="0.01" disabled={readOnly} addonBefore="$" />
                          </Form.Item>
                        </Col>
                        <Col xs={24} lg={12}>
                          <Form.Item label="Cost Type" name="cost_type">
                            <Select allowClear disabled={readOnly}>
                              <Option value="Fixed">Fixed</Option>
                              <Option value="Time and Material">Time and Material</Option>
                              <Option value="Internal">Internal</Option>
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col xs={24} lg={12}>
                          <Form.Item
                            label="Cost Account"
                            name="cost_account"
                          >
                            <Select
                              allowClear
                              disabled={readOnly}
                              placeholder={
                                mergedCostAccountOptions.length
                                  ? "Select cost account"
                                  : "Select equipment first (or open from a facility context)"
                              }
                              showSearch
                              optionFilterProp="children"
                            >
                              {mergedCostAccountOptions.map((row) => (
                                <Option key={row.code} value={row.code}>
                                  {row.label}
                                  {!row.is_builtin ? ` (${row.code})` : ""}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col xs={24} lg={12}>
                          <Form.Item label="Labor Cost" name="labor_cost">
                            <Input type="number" min={0} step="0.01" disabled={readOnly} addonBefore="$" />
                          </Form.Item>
                        </Col>
                        <Col xs={24} lg={12}>
                          <Form.Item label="Parts Cost" name="parts_cost">
                            <Input type="number" min={0} step="0.01" disabled={readOnly} addonBefore="$" />
                          </Form.Item>
                        </Col>
                        <Col xs={24} lg={12}>
                          <Form.Item label="Vendor Cost" name="vendor_cost">
                            <Input type="number" min={0} step="0.01" disabled={readOnly} addonBefore="$" />
                          </Form.Item>
                        </Col>
                        <Col xs={24} lg={12}>
                          <Form.Item
                            label="Total Cost (actual)"
                            shouldUpdate={(prev, curr) =>
                              prev.labor_cost !== curr.labor_cost ||
                              prev.parts_cost !== curr.parts_cost ||
                              prev.vendor_cost !== curr.vendor_cost
                            }
                          >
                            {() => (
                              <span style={{ fontWeight: 600, color: "#dc2626" }}>
                                ${(
                                  parseFloat(form.getFieldValue("labor_cost") || 0) +
                                  parseFloat(form.getFieldValue("parts_cost") || 0) +
                                  parseFloat(form.getFieldValue("vendor_cost") || 0)
                                ).toFixed(2)}
                              </span>
                            )}
                          </Form.Item>
                        </Col>

                        <Col xs={24} lg={24}></Col>
                        {!isCreatePage && (
                          <>
                            <Col xs={24} lg={12}>
                              <Form.Item label="Created By" name="createdby_name">
                                <span>{workorderData?.createdby_name}</span>
                              </Form.Item>
                            </Col>
                            <Col xs={24} lg={12}>
                              <Form.Item label="Created Date" name="created_date">
                                <span>
                                  {workorderData?.created_date
                                    ? moment(workorderData.created_date).format("YYYY-MM-DD HH:mm")
                                    : ""}
                                </span>
                              </Form.Item>
                            </Col>
                            <Col xs={24} lg={12}>
                              <Form.Item label="Modified By" name="lastModified_name">
                                <span>{workorderData?.lastModified_name}</span>
                              </Form.Item>
                            </Col>
                            <Col xs={24} lg={12}>
                              <Form.Item label="Modified Date" name="modified_date">
                                <span>
                                  {workorderData?.modified_date
                                    ? moment(workorderData.modified_date).format("YYYY-MM-DD HH:mm")
                                    : ""}
                                </span>
                              </Form.Item>
                            </Col>
                          </>
                        )}
                      </Row>
                    </Form>
                  </div>
                </div>
              </div>
            </div>
          </TabPane>

          {/* Related Tab：关联的 Service Request / Tenant Request / PM（均非必须）。
              TabPane 首次激活才挂载子组件 → 进入详情页不会多打请求 */}
          {!isCreatePage && (
            <TabPane
              tab={
                <span>
                  <i className="fa-solid fa-link pr-2"></i>Related
                </span>
              }
              key="related"
            >
              <div className="px-4">
                <WorkorderRelatedTab id={id} />
              </div>
            </TabPane>
          )}

          {showPmChecklistTab && (
            <TabPane
              tab={
                <span>
                  <i className="fa-solid fa-clipboard-check pr-2" />
                  PM Checklist
                </span>
              }
              key="pm-checklist"
            >
              <div className="slds-m-around_x-small px-4">
                <Divider orientation="left" style={{ fontSize: 13, color: "#666" }}>
                  <i className="fa-solid fa-clipboard-check" style={{ marginRight: 6 }} />
                  {workorderData?.pm_checklist_result?.checklist_name
                    || workorderData?.pm_plan_name
                    || "PM Checklist"}
                </Divider>
                {workorderData?.pm_checklist_result?.checklist_description ? (
                  <div
                    style={{
                      marginBottom: 16,
                      color: "#666",
                      fontSize: 13,
                      lineHeight: 1.55,
                    }}
                  >
                    {workorderData.pm_checklist_result.checklist_description}
                  </div>
                ) : null}
                {pmChecklistView.mode === "empty" ||
                (pmChecklistView.mode === "cards" &&
                  (!pmChecklistView.cards || pmChecklistView.cards.length === 0)) ? (
                  <div style={{ color: "#999", marginBottom: 16 }}>No checklist items.</div>
                ) : pmChecklistView.mode === "table" ? (
                  renderPmChecklistMatrixTable(pmChecklistView)
                ) : (
                  <Row gutter={[16, 16]}>
                    {pmChecklistView.cards.map((row, idx) => renderPmChecklistItem(row, idx))}
                  </Row>
                )}
              </div>
            </TabPane>
          )}

          {/* Comments, Attachments and Log tabs - Hidden in create mode */}
          {!isCreatePage && (
            <>
              <TabPane
                tab={
                  <>
                    <Badge className='__comment_badge__' count={unreadCommentIds.length} offset={[0, 0]}>
                    <span>
                        <i 
                          className={hasComments ? "fas fa-comment-dots pr-2" : "fa-regular fa-comment-dots pr-2"}
                          style={{ color: hasComments ? "#faad14" : "inherit" }}
                        ></i>
                        Comments
                    </span>
                  </Badge>
                  </>
                }
                key="2"
              >
                <div className="px-4">
                  <WorkorderComment
                    id={id}
                    equipmentId={workorderData?.equipments?.[0]}
                  />
                </div>
              </TabPane>

              <TabPane
                tab={
                  <span>
                    <i className="fa-solid fa-file-pdf pr-2"></i>Attachments
                  </span>
                }
                key="3"
              >
                {workorderData ? (
                  <ResponsiveFileManagement
                    tableName="workorder"
                    recordId={id}
                    equipmentId={workorderData?.equipments?.[0]}
                    permissionCode="workorder"
                    fileManageMode="scoped"
                  />
                ) : (
                  ""
                )}
              </TabPane>

              <TabPane
                tab={
                  <span>
                    <i className="fa-solid fa-timeline pr-2"></i>Log
                  </span>
                }
                key="4"
              >
                <div className="px-4">
                  <RecordAuditTimeline moduleId={id} module="Workorder" />
                </div>
              </TabPane>
            </>
          )}
        </DetailTabs>
        {!isCreatePage && workorderData?.contractor != null ? (
          <ContractCompanyDetailModal
            open={contractorDetailOpen}
            onClose={() => setContractorDetailOpen(false)}
            contractor={{
              id: workorderData.contractor,
              name: workorderData.contractor_name,
              email: workorderData.contractor_email,
              logo: workorderData.contractor_logo,
            }}
            facilityId={facilityId}
            companyId={companyId}
          />
        ) : null}
      </Spin>
    </div>
  );
};

export default WorkorderDetail;
